import React from 'react';
import './OfferCard.css';
import './AiOfferRecommendationPanel.css';

const CHANNEL_LABELS = {
  AT_HOME: 'Home Feed',
  IN_MALL: 'In-Store',
  IN_QUEUE: 'Active Queue'
};

const BUCKET_SHORT_LABELS = {
  DORMANT: 'Dormant customers',
  SLIPPING_AWAY: 'Slipping-away customers',
  AT_RISK: 'At-risk customers',
  DUE_SOON: 'Due-soon customers',
  LOYAL_ON_TIME: 'Loyal regulars',
  HIGH_SPEND: 'High spenders',
  OFFER_SENSITIVE: 'Offer-sensitive customers'
};

const formatGeneratedDate = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(date);
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '—';
  }
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return `${Math.round(percent)}%`;
};

const formatScope = (draft = {}) => {
  const scope = String(draft?.appliesOnType || '').trim().toUpperCase();
  if (scope === 'CATEGORIES') {
    return draft?.appliesOnCategories ? `Specific categories: ${draft.appliesOnCategories}` : 'Specific categories';
  }
  if (scope === 'PRODUCTS') {
    return draft?.appliesOnProducts ? `Specific services: ${draft.appliesOnProducts}` : 'Specific services';
  }
  if (scope === 'MIN_CART') {
    return 'Minimum cart value';
  }
  return 'Store-wide (all items)';
};

const formatAudience = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return 'All customers';
  }
  return text
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatMoney = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return `Rs ${Math.round(numeric).toLocaleString('en-IN')}`;
};

const formatRedemptionLimit = (draft = {}) => {
  const type = String(draft?.redemptionLimitType || 'none').trim().toLowerCase();
  const value = Number(draft?.redemptionLimitValue);
  if (!Number.isFinite(value) || value <= 0 || type === 'none') {
    return null;
  }
  if (type === 'per_user') {
    return `${Math.round(value)} per customer`;
  }
  if (type === 'global') {
    return `${Math.round(value)} total claims`;
  }
  return `${Math.round(value)} claims`;
};

const formatMode = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }
  return text
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const buildRuleDetails = (draft = {}) => ([
  { label: 'Max save', value: formatMoney(draft.maxDiscountCap) },
  { label: 'Claim cap', value: formatRedemptionLimit(draft) },
  { label: 'Budget', value: formatMoney(draft.budgetCeiling) },
  { label: 'Min discount', value: formatMoney(draft.minDiscountFloor) },
  { label: 'Redemption', value: formatMode(draft.redemptionMode) },
  { label: 'Stacking', value: formatMode(draft.stackingMode) }
]).filter((item) => item.value);

const AiOfferRecommendationCard = ({
  recommendation,
  onPublish,
  onDismiss,
  publishDisabled = false
}) => {
  const draft = recommendation?.draft || {};
  const channels = Array.isArray(draft.channels) ? draft.channels : [];
  const isPublished = Boolean(recommendation?.isPublished || recommendation?.status === 'PUBLISHED');
  const confidence = recommendation?.confidence ?? draft?.confidence;
  const ruleDetails = buildRuleDetails(draft);
  const targetLabel = BUCKET_SHORT_LABELS[String(draft?.targetRetentionBucket || '').toUpperCase()] || 'All customers';
  const generatedOn = formatGeneratedDate(recommendation?.createdAt);

  return (
    <article className={`offer-card offer-card-shell ai-offer-card ${isPublished ? 'ai-offer-card-published' : 'offer-card-state-paused'}`}>
      <div className="offer-card-header">
        <div className="offer-card-badges">
          <span className="offer-type-pill ai">AI draft</span>
          <span className="offer-type-pill ai-offer-target-pill">For: {targetLabel}</span>
          <span className={`offer-status-pill ${isPublished ? 'active' : 'paused'}`}>
            <span className="offer-status-pill-dot" aria-hidden="true" />
            <span className="offer-status-pill-text">{isPublished ? 'Published' : 'Not published'}</span>
          </span>
        </div>
        <div className="offer-card-actions">
          {!isPublished ? (
            <button
              className="btn ghost small"
              type="button"
              onClick={() => onDismiss?.(recommendation)}
              disabled={publishDisabled || !recommendation?.id}
            >
              Dismiss
            </button>
          ) : null}
          <button
            className="btn primary small"
            type="button"
            onClick={() => onPublish?.(recommendation)}
            disabled={publishDisabled || isPublished || !recommendation?.id}
          >
            {isPublished ? 'Published' : 'Publish'}
          </button>
        </div>
      </div>

      {generatedOn ? (
        <p className="ai-offer-generated-on">Generated {generatedOn}</p>
      ) : null}

      <h4 className="offer-card-title">{draft.title || 'Untitled smart offer'}</h4>
      {draft.description ? <p className="offer-card-description">{draft.description}</p> : null}
      <p className="offer-card-description offer-card-description-secondary">{formatScope(draft)}</p>

      <div className="offer-card-channels" aria-label="Placement channels">
        {channels.length === 0 ? (
          <span className="offer-card-muted">No channels selected</span>
        ) : (
          channels.map((channel) => (
            <span key={channel} className="offer-channel-pill">
              {CHANNEL_LABELS[channel] || channel}
            </span>
          ))
        )}
      </div>

      <div className="offer-card-details">
        <div className="offer-card-detail">
          <span className="offer-card-label">Offer</span>
          <strong className="offer-card-value">{draft.discount || '—'}</strong>
        </div>
        <div className="offer-card-detail">
          <span className="offer-card-label">Audience</span>
          {/* CP-111: bucket-targeted drafts wear the bucket name, not "Repeat"/"All". */}
          <strong className="offer-card-value">
            {draft.targetRetentionBucket
              ? (BUCKET_SHORT_LABELS[String(draft.targetRetentionBucket).toUpperCase()] || draft.targetRetentionBucket)
              : formatAudience(draft.audience)}
          </strong>
        </div>
        <div className="offer-card-detail">
          <span className="offer-card-label">Confidence</span>
          <strong className="offer-card-value">{formatPercent(confidence)}</strong>
        </div>
      </div>

      {ruleDetails.length > 0 ? (
        <div className="ai-offer-card-rules" aria-label="AI offer rules">
          {ruleDetails.map((rule) => (
            <div key={rule.label} className="ai-offer-card-rule">
              <span>{rule.label}</span>
              <strong>{rule.value}</strong>
            </div>
          ))}
        </div>
      ) : null}

      <div className="ai-offer-card-insight-grid">
        <div className="ai-offer-card-insight">
          <span className="offer-card-label">Reason</span>
          <p>{draft.reason || recommendation?.reasonSummary || 'Generated from store context.'}</p>
        </div>
        <div className="ai-offer-card-insight">
          <span className="offer-card-label">Expected impact</span>
          <p>{draft.expectedImpact || 'Improve offer engagement.'}</p>
        </div>
      </div>
    </article>
  );
};

export default AiOfferRecommendationCard;
